import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ShareButton from "@/components/feature/ShareButton";
import Lightbox from "@/components/feature/Lightbox";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { worksList } from "@/mocks/works";
import type { GalleryItem } from "@/mocks/gallery";

export default function WorkDetailPage() {
  const { id } = useParams();
  const work = worksList.find((item) => item.id === id);
  const [stillIndex, setStillIndex] = useState<number | null>(null);

  useDocumentMeta({
    title: work ? `${work.title} - 张柏芝影视资料详细介绍` : "作品未找到 - 张柏芝影视资料",
    description: work
      ? `详细介绍张柏芝参演的影视作品《${work.title}》，包含剧情、角色与图片展示。${work.synopsis}`
      : "未找到该部张柏芝参演的影视作品，请返回作品列表浏览完整档案。",
    keywords: work ? `张柏芝,${work.title},${work.type},张柏芝影视资料,${work.role}` : "张柏芝影视资料",
  });

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  if (!work) {
    return (
      <SiteLayout>
        <div className="flex w-full flex-col items-center justify-center px-4 py-32 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-film-line text-2xl" aria-hidden="true"></i>
          </span>
          <h1 className="mt-5 font-heading text-xl font-semibold text-foreground-950">没有找到这部作品</h1>
          <p className="mt-2 text-sm text-foreground-600">链接可能已失效，或该作品尚未归档。</p>
          <Link
            to="/works"
            className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50"
          >
            返回作品列表
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const lightboxItems: GalleryItem[] = work.stills.map((still, index) => ({
    id: `${work.id}-still-${index}`,
    category: "宣传照",
    title: still.alt,
    date: `${work.year}`,
    image: still.src,
    caption: `${work.title} · ${work.year} · 饰演 ${work.role}`,
    ratio: "landscape",
  }));

  const related = worksList
    .filter((item) => item.id !== work.id && (item.type === work.type || item.heat >= 80))
    .slice(0, 3);

  const facts = [
    { label: "导演", value: work.director, icon: "ri-clapperboard-line" },
    { label: "类型", value: work.type, icon: "ri-film-line" },
    { label: "年份", value: `${work.year}`, icon: "ri-calendar-line" },
    { label: "合作演员", value: work.coStars.join(" / "), icon: "ri-group-line" },
  ];

  return (
    <SiteLayout>
      <article className="w-full">
        <section className="relative w-full overflow-hidden bg-background-100">
          <div className="pointer-events-none absolute -right-24 -top-16 h-72 w-72 rounded-full bg-primary-100/70 blur-3xl"></div>
          <div className="relative grid w-full grid-cols-1 gap-8 px-4 py-8 md:px-8 md:py-12 lg:grid-cols-[300px_1fr] lg:px-12">
            <div className="mx-auto w-full max-w-[260px] overflow-hidden rounded-lg bg-background-200 lg:mx-0">
              <img
                src={work.cover}
                alt={`${work.title} 海报 - 张柏芝影视作品`}
                title={`${work.title} - 张柏芝影视作品海报`}
                className="aspect-[3/4] h-full w-full object-cover object-top"
              />
            </div>

            <div className="flex min-w-0 flex-col">
              <Breadcrumb items={[{ label: "影视作品", to: "/works" }, { label: work.title }]} />

              <div className="mt-4 flex flex-wrap items-center gap-2 text-[11px]">
                <span className="rounded-full bg-primary-500 px-3 py-1 text-background-50">{work.highlight}</span>
                <span className="rounded-full bg-background-50 px-3 py-1 text-foreground-700">
                  {work.type} · {work.year}
                </span>
                <span className="rounded-full bg-background-50 px-3 py-1 text-foreground-700">热度 {work.heat}</span>
              </div>

              <h1 className="mt-4 font-heading text-[26px] font-semibold leading-snug text-foreground-950 md:text-[36px]">
                {work.title}
              </h1>
              {work.enTitle && (
                <p className="mt-1 text-xs uppercase tracking-[0.24em] text-foreground-500">{work.enTitle}</p>
              )}

              <p className="mt-4 max-w-3xl text-sm leading-7 text-foreground-700">{work.synopsis}</p>

              <dl className="mt-6 grid grid-cols-2 gap-3 border-t border-background-300/70 pt-5 sm:grid-cols-4">
                {facts.map((fact) => (
                  <div key={fact.label}>
                    <dt className="flex items-center gap-1 text-[11px] text-foreground-500">
                      <i className={`${fact.icon} text-primary-600`} aria-hidden="true"></i>
                      {fact.label}
                    </dt>
                    <dd className="mt-1 text-xs leading-6 text-foreground-900">{fact.value}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <ShareButton title={work.title} />
                <Link
                  to="/works"
                  className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-grid-line" aria-hidden="true"></i>
                  全部作品
                </Link>
              </div>
            </div>
          </div>
        </section>

        <div className="grid w-full grid-cols-1 gap-10 px-4 py-10 md:px-8 md:py-14 lg:grid-cols-[1fr_320px] lg:px-12">
          <div className="min-w-0">
            <section aria-label="角色介绍">
              <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.28em] text-primary-600">
                <span className="h-[1px] w-6 bg-primary-400"></span>
                The Role
              </div>
              <h2 className="mt-3 font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                张柏芝 饰 {work.role}
              </h2>
              <div className="mt-4 rounded-lg border-l-2 border-primary-400 bg-background-100 p-5">
                <p className="text-[14px] leading-8 text-foreground-800">{work.roleIntro}</p>
              </div>
            </section>

            <section aria-label="剧照" className="mt-10">
              <div className="flex items-end justify-between">
                <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">剧照</h2>
                <span className="text-[11px] text-foreground-500">点击图片查看大图并左右翻页</span>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {work.stills.map((still, index) => (
                  <button
                    key={still.src}
                    type="button"
                    onClick={() => setStillIndex(index)}
                    className="group relative h-52 cursor-pointer overflow-hidden rounded-lg bg-background-200 sm:h-60"
                  >
                    <img
                      src={still.src}
                      alt={`${still.alt} - 张柏芝影视剧照`}
                      title={`${work.title} 剧照`}
                      className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                    <span className="absolute bottom-3 left-3 flex items-center gap-1.5 rounded-full bg-foreground-950/60 px-2.5 py-1 text-[11px] text-background-100 backdrop-blur">
                      <i className="ri-zoom-in-line" aria-hidden="true"></i>
                      查看大图
                    </span>
                  </button>
                ))}
              </div>
            </section>

            <section aria-label="相关推荐" className="mt-10">
              <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">相关推荐作品</h2>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {related.map((item) => (
                  <Link
                    key={item.id}
                    to={`/works/${item.id}`}
                    className="group overflow-hidden rounded-lg border border-background-200 transition-colors hover:border-primary-300"
                  >
                    <div className="h-56 w-full overflow-hidden bg-background-200">
                      <img
                        src={item.cover}
                        alt={`${item.title} 海报 - 张柏芝影视作品`}
                        title={`${item.title} - 张柏芝影视作品`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4">
                      <p className="text-[11px] text-foreground-500">
                        {item.year} · {item.type}
                      </p>
                      <p className="mt-1 font-heading text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-700">
                        {item.title}
                      </p>
                      <p className="mt-1 line-clamp-1 text-[11px] text-foreground-500">饰演 {item.role}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          </div>

          <aside className="flex flex-col gap-5 lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-lg bg-secondary-100 p-5">
              <h2 className="font-heading text-sm font-semibold text-foreground-950">作品档案</h2>
              <ul className="mt-3 space-y-3 text-xs">
                <li className="flex items-center justify-between border-b border-background-300/60 pb-2.5">
                  <span className="text-foreground-600">作品名称</span>
                  <span className="text-foreground-900">{work.title}</span>
                </li>
                <li className="flex items-center justify-between border-b border-background-300/60 pb-2.5">
                  <span className="text-foreground-600">上映年份</span>
                  <span className="text-foreground-900">{work.year}</span>
                </li>
                <li className="flex items-center justify-between border-b border-background-300/60 pb-2.5">
                  <span className="text-foreground-600">作品类型</span>
                  <span className="text-foreground-900">{work.type}</span>
                </li>
                <li className="flex items-start justify-between gap-4">
                  <span className="shrink-0 text-foreground-600">合作演员</span>
                  <span className="text-right text-foreground-900">{work.coStars.join("、")}</span>
                </li>
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 p-5">
              <h2 className="font-heading text-sm font-semibold text-foreground-950">继续浏览</h2>
              <ul className="mt-3 space-y-2">
                {worksList
                  .filter((item) => item.id !== work.id)
                  .slice(0, 3)
                  .map((item) => (
                    <li key={item.id}>
                      <Link
                        to={`/works/${item.id}`}
                        className="group flex items-center justify-between gap-3 rounded-md px-2 py-2 transition-colors hover:bg-background-100"
                      >
                        <span className="min-w-0">
                          <span className="block truncate text-xs text-foreground-800 transition-colors group-hover:text-primary-700">
                            {item.title}
                          </span>
                          <span className="mt-0.5 block text-[11px] text-foreground-500">
                            {item.year} · {item.type}
                          </span>
                        </span>
                        <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true"></i>
                      </Link>
                    </li>
                  ))}
              </ul>
            </div>
          </aside>
        </div>
      </article>

      {stillIndex !== null && (
        <Lightbox
          items={lightboxItems}
          index={stillIndex}
          onClose={() => setStillIndex(null)}
          onPrev={() =>
            setStillIndex((current) => ((current ?? 0) - 1 + lightboxItems.length) % lightboxItems.length)
          }
          onNext={() => setStillIndex((current) => ((current ?? 0) + 1) % lightboxItems.length)}
          onSelect={(target) => setStillIndex(target)}
        />
      )}
    </SiteLayout>
  );
}